import { formatCount, tt } from '@agos/shared'

import type { BoardOrder } from './board'
import { canReassignDriver } from './dispatch-rules'

/**
 * Giving several orders to one driver in one action.
 *
 * The board already dispatches in runs — *"one request per driver, because a driver leaving with three
 * bags is one journey"* — but every order had to be **assigned** one at a time first, from its own row.
 * A dispatcher in a rush picks the three bags going the same way and names the driver once.
 *
 * **There is no bulk-assign endpoint, and that is fine.** Assignment is `PATCH /orders/{orderId}` with
 * `driverId`; `POST /orders/bulk-dispatch` is a different thing — it moves already-assigned orders on
 * the road. So this is N patches, and the interesting part is what happens when one of them fails: the
 * others still go, and the screen says which did not, because a dispatcher needs the bags that *can*
 * leave to leave. That is the same rule `dispatchSelected` already follows.
 *
 * **An order that already has a driver is not silently re-pointed.** Re-assigning is legitimate — a
 * driver calls in sick — but doing it to five orders because they happened to be selected is not, so the
 * ones that would change hands are counted and named before the button is pressed.
 *
 * **And §14.5's lock applies here exactly as it does to one row.** `canReassignDriver` refuses an order
 * whose driver is already on the road: *"the credit belongs to the journey somebody is making"*, and it
 * has to come back through the driver's own screen. A bulk action that quietly skipped that rule would
 * be the one place in the port where selecting more orders let a dispatcher do more than selecting one.
 * Locked orders are counted into their own bucket and named.
 */

export interface AssignPlan {
  /** Orders with no driver — the plain case. */
  fresh: number[]
  /** Orders already assigned to somebody else, which this would take away from them. */
  reassigned: number[]
  /** Orders already on this driver — nothing to do. */
  unchanged: number[]
  /** Orders whose driver is already on the road — §14.5 will not let this screen move them. */
  locked: number[]
}

export function planFor(
  orders: readonly BoardOrder[],
  driverId: number,
): AssignPlan {
  const plan: AssignPlan = { fresh: [], reassigned: [], unchanged: [], locked: [] }
  for (const order of orders) {
    const id = order.id ?? 0
    if (id === 0) continue
    const current = order.driverId ?? null
    if (current === driverId) plan.unchanged.push(id)
    else if (current === null) plan.fresh.push(id)
    else if (!canReassignDriver(order)) plan.locked.push(id)
    else plan.reassigned.push(id)
  }
  return plan
}

/** The orders this action would actually write. */
export function orderIdsToWrite(plan: AssignPlan): number[] {
  return [...plan.fresh, ...plan.reassigned]
}

/**
 * What the button is about to do, said before it is pressed.
 *
 * The re-assignment is called out separately because it takes work away from a driver who may already
 * be holding the bag.
 */
export function planNote(plan: AssignPlan, driverName: string): string {
  const writing = orderIdsToWrite(plan)
  if (writing.length === 0) {
    if (plan.locked.length > 0) {
      return `${formatCount(plan.locked.length)} ${tt('vybraných má šoféra na ceste - tie musí vrátiť on, nie dispečer.')}`
    }
    return plan.unchanged.length === 0
      ? tt('Vyberte objednávky.')
      : `${tt('Všetky vybrané už má')} ${driverName}.`
  }
  const parts = [`${formatCount(plan.fresh.length)} ${tt('nepriradených')}`]
  if (plan.reassigned.length > 0) {
    parts.push(`${formatCount(plan.reassigned.length)} ${tt('sa preberie inému šoférovi')}`)
  }
  if (plan.unchanged.length > 0) {
    parts.push(`${formatCount(plan.unchanged.length)} ${tt('už má')}`)
  }
  if (plan.locked.length > 0) {
    parts.push(
      `${formatCount(plan.locked.length)} ${tt('sa nedá prebrať - šofér je na ceste, musí ju vrátiť sám')}`,
    )
  }
  return `${driverName}: ${parts.join(', ')}.`
}

/** Said after the run, naming what did not go rather than reporting a flat success. */
export function outcomeNote(written: number, failed: number): string {
  if (failed === 0) return `${tt('Priradené')}: ${formatCount(written)}`
  return `${tt('Priradené')}: ${formatCount(written)}. ${formatCount(failed)} ${tt('sa nepodarilo - skúste ich jednotlivo.')}`
}

export function mayAssign(plan: AssignPlan): boolean {
  return orderIdsToWrite(plan).length > 0
}
